import { memo, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useLeaderboardByTags } from "src/services";
import { useInViewOnce } from "src/utils/useInViewOnce.js";
import { useDimensions } from "src/utils/useDimensions.js";
import LeaderboardRail, { LeaderboardRailSkeleton } from "./LeaderboardRail.js";
import { toTileCalendar } from "./calendarTileUtils.js";

/** Poster tiles per league leaderboard, matching the top of a league tag page. */
const LEADERBOARD_SIZE = 8;

/**
 * Start the individual request well before the row is visible. SectionFeed also
 * warms the next two rows during idle; this is the fallback for later rows.
 */
const RAIL_ROOT_MARGIN = "1400px";

/** Stable vertical slots prevent a late leaderboard response from moving rows below it. */
const LEADERBOARD_SLOT_HEIGHT = { compact: 313, wide: 361 };
/**
 * One league's "Most Popular in {league}" leaderboard, the same ranked poster
 * rail that tops a league tag page, dropped into the home feed. Fetches its own
 * leaderboard only once it nears the viewport, so interspersing many leagues
 * doesn't fan out a request per league on first paint.
 *
 * @param {{ tag: string, title: string, section: object }} props
 */
const LeagueLeaderboardRail = ({ tag, title, section }) => {
  const [ref, nearView] = useInViewOnce({ rootMargin: RAIL_ROOT_MARGIN });
  const { isMediumScreen } = useDimensions();
  const { data, isFetched, isError } = useLeaderboardByTags(nearView ? [tag] : []);
  const calendars = useMemo(
    () =>
      (data ?? [])
        .slice(0, LEADERBOARD_SIZE)
        .map(({ calendar }) => toTileCalendar(calendar)),
    [data],
  );

  // Once the prefetch/lazy request has definitively found no content, collapse
  // this synthetic row while it is still well above the viewport.
  if (nearView && !calendars.length && (isFetched || isError)) return null;

  return (
    <Box
      ref={ref}
      component="section"
      data-testid="league-leaderboard"
      sx={{
        mb: 7,
        // Reserve the slot only until content arrives, so a loaded rail hugs
        // its content instead of keeping the taller skeleton's dead space.
        minHeight: calendars.length
          ? undefined
          : `${isMediumScreen ? LEADERBOARD_SLOT_HEIGHT.compact : LEADERBOARD_SLOT_HEIGHT.wide}px`,
      }}
    >
      <Typography variant="sectionTitle" component="h2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {calendars.length > 0 ? (
        <LeaderboardRail calendars={calendars} section={section} />
      ) : (
        <LeaderboardRailSkeleton />
      )}
    </Box>
  );
};

// `memo`: the feed re-renders as pages load; a leaderboard's props are stable,
// so it shouldn't re-render (and refetch) when a sibling section changes.
export default memo(LeagueLeaderboardRail);
